import { useEffect, useMemo } from 'react';

import {
  useGetCompanyAtsKeyQuery,
  useGetCompanyAtsSyncStatusQuery,
  useGetGemProjectsQuery,
  useGetGemUsersQuery,
} from 'services/companyAtsV2';

export const useAtsSettings = (company, justSaved, setJustSaved) => {
  const {
    data: atsStatus,
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    isUninitialized: isUninitializedStatus,
  } = useGetCompanyAtsKeyQuery({ companyId: company?.id }, { skip: !company?.id });
  const hasAtsKey = atsStatus?.hasKey;
  const atsProvider = atsStatus?.provider;

  useEffect(() => {
    if (!hasAtsKey) setJustSaved(false);
  }, [hasAtsKey, setJustSaved]);

  const { data: syncStatus } = useGetCompanyAtsSyncStatusQuery({ companyId: company?.id }, { skip: !company?.id });

  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
    isUninitialized: isUninitializedProjects,
  } = useGetGemProjectsQuery({ companyId: company?.id }, { skip: !company?.id || !hasAtsKey });

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isUninitialized: isUninitializedUsers,
  } = useGetGemUsersQuery({ companyId: company?.id }, { skip: !company?.id || !hasAtsKey });

  const shouldShowSkeleton =
    !company ||
    isUninitializedStatus ||
    isFetchingStatus ||
    isLoadingStatus ||
    (hasAtsKey &&
      (isUninitializedProjects ||
        isFetchingProjects ||
        isLoadingProjects ||
        isUninitializedUsers ||
        isFetchingUsers ||
        isLoadingUsers));

  const derivedState = useMemo(() => {
    const savedProjectId = atsStatus?.gemProjectId ?? atsStatus?.gem_project_id ?? null;
    const savedUserId = atsStatus?.gemUserId ?? atsStatus?.gem_user_id ?? null;

    const projectOptions = (projectsData?.projects || []).map((p) => ({ label: p.name, value: p.id }));
    const userOptions = (usersData?.users || []).map((u) => ({ label: u.name, value: u.id }));

    const initialProjectOption = projectOptions.find((o) => o.value === savedProjectId) || null;
    const initialUserOption = userOptions.find((o) => o.value === savedUserId) || null;

    const isGemSelectionLocked = Boolean(savedProjectId && savedUserId);

    return {
      atsProvider,
      hasAtsKey,
      syncStatus,
      projectOptions,
      userOptions,
      initialProjectOption,
      initialUserOption,
      isGemSelectionLocked,
      savedProjectId,
    };
  }, [atsStatus, projectsData, usersData, atsProvider, hasAtsKey, syncStatus]);

  return { shouldShowSkeleton, ...derivedState };
};
